import React, { useState } from 'react';

const ProductCard = React.memo(({ product, view = 'grid' }) => {
  const [showMore, setShowMore] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const placeholderImageUrl = 'https://firebasestorage.googleapis.com/v0/b/psychic-diorama-468314-e2.firebasestorage.app/o/images%2Fplaceholder.png?alt=media';

  const packagingText = product.packaging && product.packaging.length > 0
    ? product.packaging.join(', ')
    : 'Various options available';
  
  const description = product.description || 'No description available.';
  const isLongDescription = description.length > 100;
  const displayDescription = (view === 'grid' && isLongDescription && !showMore)
    ? `${description.substring(0, 97)}...`
    : description;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Sora:wght@400;500;600;700;800&display=swap');

        .product-card {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          border-radius: 20px;
          border: 1px solid rgba(0, 0, 0, 0.06);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02), 0 4px 12px rgba(0, 0, 0, 0.03), 0 12px 32px rgba(0, 0, 0, 0.04);
          overflow: hidden;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          width: 100%;
          text-align: left;
          display: flex;
          position: relative;
          backdrop-filter: blur(10px);
        }

        .badge {
          position: absolute;
          top: 16px;
          right: 16px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          padding: 6px 14px;
          border-radius: 12px;
          font-family: 'Sora', sans-serif;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.5px;
          z-index: 10;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2);
          overflow: hidden;
        }

        .badge-shine {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
          animation: shine 3s infinite;
        }

        @keyframes shine {
          0%, 100% { left: -100%; }
          50% { left: 100%; }
        }

        .image-skeleton {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s infinite;
        }

        @keyframes skeleton-loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        .product-card.grid-view {
          flex-direction: column;
          margin: 0 auto;
        }

        .product-card.grid-view:hover {
          transform: translateY(-8px) scale(1.01);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06), 0 12px 32px rgba(0, 0, 0, 0.08), 0 24px 64px rgba(0, 0, 0, 0.1);
          border-color: rgba(0, 0, 0, 0.1);
        }

        .product-card.grid-view .product-image-container {
          width: 100%;
          aspect-ratio: 4 / 3;
          overflow: hidden;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          position: relative;
        }

        .product-card.grid-view .product-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          opacity: 0;
        }

        .product-card.grid-view .product-image.loaded {
          opacity: 1;
        }

        .product-card.grid-view:hover .product-image {
          transform: scale(1.08);
        }

        .image-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.02) 100%);
          opacity: 0;
          transition: opacity 0.4s ease;
          pointer-events: none;
        }

        .product-card:hover .image-overlay {
          opacity: 1;
        }

        .product-card.grid-view .product-card-content {
          padding: 28px 32px 32px;
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          background: white;
        }

        .product-card.list-view {
          flex-direction: row;
          max-width: none;
          align-items: stretch;
        }

        .product-card.list-view:hover {
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06), 0 12px 40px rgba(0, 0, 0, 0.08);
          border-color: rgba(0, 0, 0, 0.08);
          transform: translateX(4px);
        }

        .product-card.list-view .product-image-container {
          width: 280px;
          flex-shrink: 0;
          aspect-ratio: 4 / 3;
          border-right: 1px solid rgba(0, 0, 0, 0.06);
          position: relative;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        }

        .product-card.list-view .product-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          opacity: 0;
        }

        .product-card.list-view .product-image.loaded {
          opacity: 1;
        }

        .product-card.list-view:hover .product-image {
          transform: scale(1.05);
        }

        .product-card.list-view .product-card-content {
          padding: 32px 36px;
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          background: white;
        }

        .product-header {
          margin-bottom: 20px;
          border-bottom: 2px solid #f1f3f5;
          padding-bottom: 16px;
        }

        .product-title {
          font-family: 'Sora', sans-serif;
          color: #1a1a1a;
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 0 8px 0;
          line-height: 1.3;
          letter-spacing: -0.02em;
          transition: color 0.3s ease;
        }

        .product-card:hover .product-title {
          color: #0066cc;
        }

        .scientific-name {
          font-family: 'Inter', sans-serif;
          font-style: italic;
          color: #6c757d;
          font-size: 0.95rem;
          font-weight: 400;
          margin: 0;
          letter-spacing: 0.01em;
        }

        .product-details {
          display: flex;
          flex-direction: column;
          gap: 16px;
          flex-grow: 1;
        }

        .detail-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 16px;
          background: #f8f9fa;
          border-radius: 12px;
          border: 1px solid rgba(0, 0, 0, 0.04);
          transition: all 0.3s ease;
        }

        .detail-item:hover {
          background: #e9ecef;
          border-color: rgba(0, 0, 0, 0.08);
          transform: translateX(4px);
        }

        .detail-icon {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
          stroke-width: 2;
          margin-top: 2px;
        }

        .origin-item .detail-icon {
          stroke: #0066cc;
        }

        .packaging-item .detail-icon {
          stroke: #10b981;
        }

        .detail-label {
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 0.875rem;
          color: #495057;
          letter-spacing: 0.02em;
        }

        .detail-value {
          font-family: 'Inter', sans-serif;
          font-weight: 400;
          font-size: 0.875rem;
          color: #212529;
          flex: 1;
        }

        .product-description {
          font-family: 'Inter', sans-serif;
          color: #495057;
          font-size: 0.95rem;
          line-height: 1.7;
          margin: 0;
          font-weight: 400;
        }

        .show-more-btn {
          background: none;
          border: none;
          color: #0066cc;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 0.875rem;
          padding: 4px 0;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-left: 6px;
          transition: all 0.3s ease;
          position: relative;
        }

        .show-more-btn:hover {
          color: #0052a3;
          transform: translateX(2px);
        }

        .show-more-btn::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 2px;
          background: #0066cc;
          transition: width 0.3s ease;
        }

        .show-more-btn:hover::after {
          width: 100%;
        }

        @media (max-width: 768px) {
          .product-card.list-view {
            flex-direction: column;
          }
          
          .product-card.list-view .product-_image-container {
            width: 100%;
            border-right: none;
            border-bottom: 1px solid rgba(0, 0, 0, 0.06);
          }
          
          .product-card.list-view .product-card-content {
            padding: 24px;
          }
          
          .product-title {
            font-size: 1.35rem;
          }
          
          .detail-item {
            padding: 10px 14px;
          }
        }
      `}</style>

      <div className={`product-card ${view}-view`}>
        {product.isNew && (
          <span className="badge">
            <span className="badge-shine"></span>
            NEW
          </span>
        )}

        <div className="product-image-container">
          {!imageLoaded && <div className="image-skeleton"></div>}
          <img
            src={product.image || placeholderImageUrl}
            alt={product.name}
            className={`product-image ${imageLoaded ? 'loaded' : ''}`}
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              e.currentTarget.src = placeholderImageUrl;
              e.currentTarget.onerror = null;
              setImageLoaded(true);
            }}
            loading="lazy"
          />
          <div className="image-overlay"></div>
        </div>
        
        <div className="product-card-content">
          <div className="product-header">
            <h4 className="product-title">{product.name}</h4>
            <p className="scientific-name">{product.scientific}</p>
          </div>
          
          <div className="product-details">
            
            
            <p className="product-description">
              {displayDescription}
              {view === 'grid' && isLongDescription && (
                <button 
                  onClick={() => setShowMore(!showMore)} 
                  className="show-more-btn"
                  aria-expanded={showMore}
                >
                  {showMore ? '← Show Less' : 'Read More →'}
                </button>
              )}
            </p>
            
         
          </div>
        </div>
      </div>
    </>
  );
});

export default ProductCard;
